import { Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { AssetTreeChild } from '@app/core/shared/models/components-asset-tree';
import { View } from 'ol';
import Map from 'ol/Map';
import { FieldViewRouteData, FieldViewSurveyMenuData, MapViewProjectionFromData } from '../models/field-view';
import { SignalBroadcastChannelService } from './signal-broadcast-channel.service';

@Injectable({
  providedIn: 'root'
})
export class FieldViewStateService {
  private static readonly CHANNELS = {
    surveyMenuDataLoadingState: 'field-view-survey-menu-loading-state',
    routeData: 'field-view-route-data',
    surveyMenuData: 'field-view-survey-menu-data',
    projectionZone: 'field-view-projection-zone',
    mapViewProjectionData: 'field-view-map-projection-zone',
    selectedPipeline: 'field-view-selected-pipeline',
    surveyMenuLayerIsLoadingIndicator: 'field-view-survey-menu-layer-is-loading-indicator'
  };

  private readonly broadcastService = inject(SignalBroadcastChannelService);

  private readonly _defaultValues = {
    fieldViewMap: new Map(),
    mapView: new View(),
    surveyMenuDataLoadingState: undefined as FieldViewSurveyMenuData | undefined,
    routeData: [] as FieldViewRouteData[],
    surveyMenuData: [] as FieldViewSurveyMenuData[],
    projectionZone: '',
    mapViewProjectionData: undefined as MapViewProjectionFromData | undefined,
    selectedPipeline: {} as AssetTreeChild,
    surveyMenuLayerIsLoadingIndicator: false
  };

  private readonly _fieldViewMap: WritableSignal<Map> = signal(this._defaultValues.fieldViewMap);
  private readonly _mapView: WritableSignal<View> = signal(this._defaultValues.mapView);

  private readonly _routeData: WritableSignal<FieldViewRouteData[]>;
  private readonly _broadcastRouteData: (value: FieldViewRouteData[]) => void;

  private readonly _surveyMenuData: WritableSignal<FieldViewSurveyMenuData[]>;
  private readonly _broadcastSurveyMenuData: (value: FieldViewSurveyMenuData[]) => void;

  private readonly _projectionZone: WritableSignal<string>;
  private readonly _broadcastProjectionZone: (value: string) => void;

  private readonly _mapViewProjectionData: WritableSignal<MapViewProjectionFromData | undefined>;
  private readonly _broadcastMapViewProjectionData: (value: MapViewProjectionFromData | undefined) => void;

  private readonly _selectedPipeline: WritableSignal<AssetTreeChild>;
  private readonly _broadcastSelectedPipeline: (value: AssetTreeChild) => void;

  private readonly _surveyMenuLayerIsLoadingIndicator: WritableSignal<boolean>;
  private readonly _broadcastSurveyMenuLayerIsLoadingIndicator: (value: boolean) => void;

  constructor() {
    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<
        FieldViewRouteData[]
      >(FieldViewStateService.CHANNELS.routeData, this._defaultValues.routeData);
      this._routeData = signal;
      this._broadcastRouteData = broadcastUpdate;
    }
    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<
        FieldViewSurveyMenuData[]
      >(FieldViewStateService.CHANNELS.surveyMenuData, this._defaultValues.surveyMenuData);
      this._surveyMenuData = signal;
      this._broadcastSurveyMenuData = broadcastUpdate;
    }
    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<string>(
        FieldViewStateService.CHANNELS.projectionZone,
        this._defaultValues.projectionZone
      );
      this._projectionZone = signal;
      this._broadcastProjectionZone = broadcastUpdate;
    }
    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<
        MapViewProjectionFromData | undefined
      >(FieldViewStateService.CHANNELS.mapViewProjectionData, this._defaultValues.mapViewProjectionData);
      this._mapViewProjectionData = signal;
      this._broadcastMapViewProjectionData = broadcastUpdate;
    }
    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<
        AssetTreeChild | undefined
      >(FieldViewStateService.CHANNELS.selectedPipeline, this._defaultValues.selectedPipeline);
      this._selectedPipeline = signal;
      this._broadcastSelectedPipeline = broadcastUpdate;
    }
    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<boolean | undefined>(
        FieldViewStateService.CHANNELS.surveyMenuLayerIsLoadingIndicator,
        this._defaultValues.surveyMenuLayerIsLoadingIndicator
      );
      this._surveyMenuLayerIsLoadingIndicator = signal;
      this._broadcastSurveyMenuLayerIsLoadingIndicator = broadcastUpdate;
    }
  }

  get fieldViewMapState(): Signal<Map> {
    return this._fieldViewMap;
  }
  get mapViewState(): Signal<View> {
    return this._mapView;
  }

  get routeData(): Signal<FieldViewRouteData[]> {
    return this._routeData;
  }
  get surveyMenuData(): Signal<FieldViewSurveyMenuData[]> {
    return this._surveyMenuData;
  }
  get projectionZone(): Signal<string> {
    return this._projectionZone;
  }
  get mapViewProjectionData(): Signal<MapViewProjectionFromData | undefined> {
    return this._mapViewProjectionData;
  }
  get selectedPipeline(): Signal<AssetTreeChild | undefined> {
    return this._selectedPipeline;
  }

  get surveyMenuLayerIsLoadingIndicator(): Signal<boolean | undefined> {
    return this._surveyMenuLayerIsLoadingIndicator;
  }

  setFieldViewMapState(value: Map): void {
    this._fieldViewMap.set(value);
  }

  setMapViewState(value: View): void {
    this._mapView.set(value);
  }

  setRouteData(value: FieldViewRouteData[]): void {
    this._routeData.set(value);
    this._broadcastRouteData(value);
  }

  setSurveyMenuData(value: FieldViewSurveyMenuData[]): void {
    this._surveyMenuData.set(value);
    this._broadcastSurveyMenuData(value);
  }

  setProjectionZone(value: string): void {
    this._projectionZone.set(value);
    this._broadcastProjectionZone(value);
  }

  setMapViewProjectionData(value: MapViewProjectionFromData | undefined): void {
    this._mapViewProjectionData.set(value);
    this._broadcastMapViewProjectionData(value);
  }

  setSelectedPipeline(value: AssetTreeChild | undefined): void {
    this._selectedPipeline.set(value);
    this._broadcastSelectedPipeline(value);
  }

  setsurveyMenuLayerIsLoadingIndicator(value: boolean | undefined): void {
    this._surveyMenuLayerIsLoadingIndicator.set(value);
    this._broadcastSurveyMenuLayerIsLoadingIndicator(value);
  }

  updateSurveyMenuDataItemProperty(
    pipelineSurveyId: number,
    property: keyof FieldViewSurveyMenuData,
    value: boolean | string | number
  ): void {
    const updated = this._surveyMenuData().map(menuItem =>
      menuItem.pipelineSurveyId === pipelineSurveyId ? { ...menuItem, [property]: value } : menuItem
    );
    this.setSurveyMenuData(updated);
  }

  updateSurveyMenuDataCollapseAllAccordions(): void {
    const updated = this._surveyMenuData().map(menuItem => ({
      ...menuItem,
      accordionIsExanded: false
    }));
    this.setSurveyMenuData(updated);
  }

  updateAllSurveyMenuDataItemCheckedProperties(isChecked: boolean): void {
    const updated = this._surveyMenuData().map(menuItem => ({
      ...menuItem,
      eventIsChecked: menuItem.eventIsChecked !== undefined ? isChecked : menuItem.eventIsChecked,
      bathyIsChecked: menuItem.bathyIsChecked !== undefined ? isChecked : menuItem.bathyIsChecked
    }));
    this.setSurveyMenuData(updated);
  }

  // Reset methods

  resetAllToDefault(): void {
    this._routeData.set(this._defaultValues.routeData);
    this._surveyMenuData.set(this._defaultValues.surveyMenuData);
    this._projectionZone.set(this._defaultValues.projectionZone);
    this._mapViewProjectionData.set(this._defaultValues.mapViewProjectionData);
  }

  resetLayerSelectionToDefault(): void {
    this.updateAllSurveyMenuDataItemCheckedProperties(false);
  }
}
